import { useState } from 'react';
import { Link } from 'react-router-dom';

const DigitalUpgradesSection = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'carplay'>('dashboard');

  const carBrands = [
    { name: 'BMW', icon: 'https://upload.wikimedia.org/wikipedia/commons/4/44/BMW.svg' },
    { name: 'Ford', icon: 'https://upload.wikimedia.org/wikipedia/commons/3/3e/Ford_logo_flat.svg' },
    { name: 'Chevrolet', icon: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/7f/Chevrolet_logo.svg/2048px-Chevrolet_logo.svg.png' },
    { name: 'Mercedes', icon: 'https://upload.wikimedia.org/wikipedia/commons/9/90/Mercedes-Logo.svg' },
    { name: 'Audi', icon: 'https://upload.wikimedia.org/wikipedia/commons/9/92/Audi-Logo_2016.svg' },
    { name: 'Volkswagen', icon: 'https://upload.wikimedia.org/wikipedia/commons/6/6d/Volkswagen_logo_2019.svg' },
  ];

  const dashboardImages = [
    {
      id: 1,
      image: 'https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/ee78eaa9f99bdeb4e9779fd1f14ea700.png',
      title: 'BMW Digital Cluster',
      subtitle: 'And Many Other Brands Too Available',
      description: 'Full LCD digital instrument cluster with customizable displays',
      link: '/digital-dashboard-details',
    },
    {
      id: 2,
      image: 'https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/7888df96dd9476e9f25a4d04add561f9.png',
      title: 'Car Radio Multimedia Player',
      subtitle: 'For BMW and Many Other Brands',
      description: 'High-resolution touchscreen with Apple CarPlay & Android Auto',
      link: '/car-multimedia-details',
    },
  ];

  const carplayImages = [
    {
      id: 1,
      image: 'https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/ee78eaa9f99bdeb4e9779fd1f14ea700.png',
      title: 'BMW Digital Cluster',
      subtitle: 'And Many Other Brands Too Available',
      description: 'Full LCD digital instrument cluster with customizable displays',
      link: '/digital-dashboard-details',
    },
    {
      id: 2,
      image: 'https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/7888df96dd9476e9f25a4d04add561f9.png',
      title: 'Car Radio Multimedia Player',
      subtitle: 'For BMW and Many Other Brands',
      description: 'High-resolution touchscreen with Apple CarPlay & Android Auto',
      link: '/car-multimedia-details',
    },
  ];

  const features = [
    {
      icon: 'ri-plug-line',
      title: 'Plug & Play',
      description: 'No cutting original cables - connects directly to factory pins',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'OEM Quality',
      description: 'Premium components that match factory specifications',
    },
    {
      icon: 'ri-tools-line',
      title: 'Expert Installation',
      description: 'Professional installation by certified technicians',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Full Support',
      description: 'Lifetime technical support and warranty included',
    },
  ];

  return (
    <section className="py-20 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-amber-500 text-sm font-semibold tracking-wider uppercase">
            Upgrade Your Ride
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-4">
            Digital Dashboard & CarPlay Screens
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Transform your vehicle with cutting-edge digital displays. Upgrade your old dashboard 
            to a modern digital cluster without cutting any original cables - everything plugs 
            directly into your factory pins.
          </p>
        </div>

        {/* Brand Logos */}
        <div className="mb-12">
          <p className="text-center text-zinc-500 text-sm mb-6">Compatible with all major brands</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {carBrands.map((brand) => (
              <div
                key={brand.name}
                className="flex flex-col items-center gap-2 opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full p-3">
                  <img
                    src={brand.icon}
                    alt={brand.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="text-zinc-400 text-xs">{brand.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-10">
          <div className="bg-zinc-900 p-1 rounded-full inline-flex">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'dashboard'
                  ? 'bg-amber-500 text-black'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              <i className="ri-dashboard-3-line mr-2"></i>
              Digital Dashboards
            </button>
            <button
              onClick={() => setActiveTab('carplay')}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'carplay'
                  ? 'bg-amber-500 text-black'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              <i className="ri-smartphone-line mr-2"></i>
              CarPlay Screens
            </button>
          </div>
        </div>

        {/* Product Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16 max-w-5xl mx-auto">
          {(activeTab === 'dashboard' ? dashboardImages : carplayImages).map((item) => (
            <div
              key={item.id}
              className="group bg-zinc-900 rounded-xl overflow-hidden hover:ring-2 hover:ring-amber-500/50 transition-all"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <div className="p-6">
                <h3 className="text-white font-semibold text-xl mb-1">{item.title}</h3>
                <p className="text-amber-500 text-sm font-medium mb-3">{item.subtitle}</p>
                <p className="text-zinc-400 text-sm mb-4">{item.description}</p>
                <Link
                  to={item.link}
                  className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  More Info
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          {features.map((feature, index) => (
            <div
              key={index}
              className="text-center p-6 bg-zinc-900/50 rounded-xl border border-zinc-800 hover:border-amber-500/30 transition-colors"
            >
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-amber-500/10 rounded-full">
                <i className={`${feature.icon} text-2xl text-amber-500`}></i>
              </div>
              <h4 className="text-white font-medium mb-2">{feature.title}</h4>
              <p className="text-zinc-500 text-sm">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              className="px-8 py-4 bg-amber-500 text-black font-semibold rounded-full hover:bg-amber-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-2"></i>
              Book Installation
            </a>
            <a
              href="tel:+1234567890"
              className="px-8 py-4 border border-zinc-700 text-white font-semibold rounded-full hover:border-amber-500 hover:text-amber-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call for Quote
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DigitalUpgradesSection;
